// PRD 10 §3.8 — delete confirmation for a content row. Root-only (the ✕ only
// renders for Root; BE re-enforces). Mounted by the list view with the target
// content; closes itself on success/cancel. Errors surface as a toast.

import { useTranslation } from 'react-i18next';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { toast } from '@/components/feedback/useToast';

import { useDeleteContent } from '../hooks/use-health-encyclopedia-mutations';
import type { ContentSummary } from '../types/health-encyclopedia.types';
import { getEncyclopediaErrorMessage } from '../utils/error-message';

interface DeleteContentConfirmProps {
  content: ContentSummary | null;
  onClose: () => void;
}

export function DeleteContentConfirm({ content, onClose }: DeleteContentConfirmProps) {
  const { t } = useTranslation('health-encyclopedia');
  const deleteMut = useDeleteContent();

  const handleConfirm = () => {
    if (!content) return;
    deleteMut.mutate(content.eid, {
      onSuccess: () => {
        toast.success(t('deleteContent.success'));
        onClose();
      },
      onError: (e) => toast.error(getEncyclopediaErrorMessage(e, t, 'deleteContent.error')),
    });
  };

  return (
    <ConfirmModal
      open={Boolean(content)}
      title={t('deleteContent.title')}
      message={t('deleteContent.message', { title: content?.title ?? '' })}
      confirmLabel={t('deleteContent.confirm')}
      cancelLabel={t('deleteContent.cancel')}
      confirmVariant="destructive"
      loading={deleteMut.isPending}
      onConfirm={handleConfirm}
      onCancel={onClose}
    />
  );
}
